import React, { useState, useEffect } from 'react';
import { FaPlus, FaFileExcel, FaSearch } from 'react-icons/fa';
import BusinessTable from '../components/businesses/BusinessTable';
import BusinessAuditModal from '../components/businesses/BusinessAuditModal';
import { supabase } from '../services/supabase';
import adminService from '../services/admin.service';
import { useAuth } from '../context/AuthContext';

function BusinessesPage() {
  const { admin, getAdminToken } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    filterBusinesses();
  }, [searchTerm, filterStatus, businesses, dateFrom, dateTo]);

  const fetchBusinesses = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_get_businesses_full', { p_admin_token: getAdminToken() });

      if (error) throw error;

      // Filialların tam məlumatları
      const { data: locData } = await supabase
        .from('business_locations')
        .select('*')
        .order('is_main', { ascending: false });
      const locMap = {};
      (locData || []).forEach(l => {
        if (!locMap[l.business_id]) locMap[l.business_id] = [];
        locMap[l.business_id].push(l);
      });

      // Qeydiyyat məlumatları (business_applications)
      const { data: appRes } = await supabase.rpc('admin_get_approved_application_details', { p_admin_token: getAdminToken() });
      const appData = appRes?.success ? appRes.rows : [];
      const appMap = {};
      (appData || []).forEach(a => { appMap[a.email] = a; });

      // Check-in sayları
      const businessesWithStats = await Promise.all(
        data.map(async (business) => {
          const { count } = await supabase
            .from('check_ins')
            .select('*', { count: 'exact', head: true })
            .eq('business_id', business.id);

          const branches = locMap[business.id] || [];
          return {
            ...business,
            total_checkins: count || 0,
            branches,
            branch_count: branches.length,
            reg_data: appMap[business.email] || null,
          };
        })
      );

      setBusinesses(businessesWithStats);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBusinesses = () => {
    let filtered = businesses;

    // Search
    if (searchTerm) {
      filtered = filtered.filter(
        (b) =>
          b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.city.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((b) => {
        if (filterStatus === 'active') return b.is_active;
        if (filterStatus === 'inactive') return !b.is_active;
        return true;
      });
    }

    // Date range filter
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter(b => new Date(b.created_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(b => new Date(b.created_at) <= to);
    }

    setFilteredBusinesses(filtered);
  };

  const handleKillSwitch = async (businessId, reason) => {
    try {
      // Deaktiv et
      const { data, error } = await supabase.rpc('admin_set_business_active', {
        p_admin_token: getAdminToken(), p_business_id: businessId, p_active: false,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);

      // Log yaz
      await adminService.logAction(
        admin.id,
        'kill_switch',
        'business',
        businessId,
        { reason }
      );

      alert('Business uğurla deaktiv edildi!');
      fetchBusinesses();
    } catch (error) {
      console.error('Error deactivating business:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleActivate = async (business) => {
    try {
      const { data, error } = await supabase.rpc('admin_set_business_active', {
        p_admin_token: getAdminToken(), p_business_id: business.id, p_active: true,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);

      // Log yaz
      await adminService.logAction(admin.id, 'activate', 'business', business.id);

      alert('Business uğurla aktivləşdirildi!');
      fetchBusinesses();
    } catch (error) {
      console.error('Error activating business:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleEdit = (business) => {
    setEditingBusiness(business);
    setEditForm({
      // Əsas
      name:           business.name || '',
      city:           business.city || '',
      address:        business.address || '',
      phone:          business.phone || '',
      business_type:  business.business_type || 'cafe',
      cuisine_type:   business.cuisine_type || '',
      // Premium & Status
      premium_tier:       business.premium_tier ?? 0,
      admin_badges:       Array.isArray(business.admin_badges) ? business.admin_badges : [],
      is_active:          business.is_active ?? true,
      is_chain:           business.is_chain ?? false,
      is_tourist_friendly: business.is_tourist_friendly ?? false,
      is_pet_friendly:    business.is_pet_friendly ?? false,
      // Sosial & Konum
      instagram_url: business.instagram_url || '',
      maps_url:      business.maps_url || '',
      latitude:      business.latitude || '',
      longitude:     business.longitude || '',
      // Maliyyə
      total_capacity:    business.total_capacity || 0,
      service_fee:       business.service_fee ?? '',
      service_fee_type:  business.service_fee_type || 'percent',
      notification_whatsapp: business.notification_whatsapp || '',
      // Təsvir
      description:    business.description || '',
      description_en: business.description_en || '',
      // Reytinq
      rating: business.rating || 0,
      // Filiallar premium tier-ləri
      branches_tiers: (business.branches || []).reduce((acc, br) => {
        acc[br.id] = br.premium_tier ?? 0;
        return acc;
      }, {}),
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editingBusiness) return;
    try {
      const { data, error } = await supabase.rpc('admin_update_business', {
        p_admin_token: getAdminToken(),
        p_business_id: editingBusiness.id,
        p_fields: {
          name:           editForm.name,
          city:           editForm.city,
          address:        editForm.address,
          phone:          editForm.phone,
          business_type:  editForm.business_type,
          cuisine_type:   editForm.cuisine_type,
          premium_tier:   parseInt(editForm.premium_tier) || 0,
          admin_badges:   editForm.admin_badges || [],
          is_active:      editForm.is_active,
          is_chain:       editForm.is_chain,
          is_tourist_friendly: editForm.is_tourist_friendly,
          is_pet_friendly:     editForm.is_pet_friendly,
          instagram_url:  editForm.instagram_url || null,
          maps_url:       editForm.maps_url || null,
          latitude:       editForm.latitude ? parseFloat(editForm.latitude) : null,
          longitude:      editForm.longitude ? parseFloat(editForm.longitude) : null,
          total_capacity: parseInt(editForm.total_capacity) || 0,
          service_fee:    editForm.service_fee !== '' ? parseFloat(editForm.service_fee) : null,
          service_fee_type: editForm.service_fee !== '' ? editForm.service_fee_type : null,
          notification_whatsapp: editForm.notification_whatsapp || null,
          description:    editForm.description || null,
          description_en: editForm.description_en || null,
          rating:         parseFloat(editForm.rating) || 0,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);

      // Filialların premium tier-lərini yenilə
      const branchEntries = Object.entries(editForm.branches_tiers || {});
      if (branchEntries.length > 0) {
        await Promise.all(branchEntries.map(([locId, tier]) =>
          supabase.rpc('admin_update_branch_tier', {
            p_admin_token: getAdminToken(), p_location_id: locId, p_tier: parseInt(tier) || 0,
          })
        ));
      }

      await adminService.logAction(admin.id, 'edit_business', 'business', editingBusiness.id, { changes: editForm });
      alert('Business uğurla yeniləndi!');
      setShowEditModal(false);
      setEditingBusiness(null);
      fetchBusinesses();
    } catch (error) {
      console.error('Error updating business:', error);
      alert('Xəta baş verdi: ' + error.message);
    }
  };

  const handleDelete = async (business) => {
    const confirmed = window.confirm(
      `"${business.name}" hesabını tamamilə silmək istəyirsiniz?\n\nBu əməliyyat GERİ ALINMAZ:\n• Bütün filiallar silinəcək\n• Bütün menecerlər silinəcək\n• Müştəri məlumatları silinəcək\n\nDavam etmək üçün OK basın.`
    );
    if (!confirmed) return;

    try {
      // RPC funksiyası SECURITY DEFINER ilə RLS-i keçərək sil
      const { error } = await supabase.rpc('delete_business_cascade', {
        p_business_id: business.id,
      });
      if (error) throw error;

      // 5. Log yaz
      await adminService.logAction(admin.id, 'delete_business', 'business', business.id, {
        name: business.name,
        email: business.email,
      });

      alert(`✅ "${business.name}" hesabı silindi.`);
      fetchBusinesses();
    } catch (err) {
      console.error('Delete error:', err);
      alert('❌ Xəta: ' + err.message);
    }
  };

  const handleViewStats = (business) => {
    setSelectedBusiness(business);
    setShowAuditModal(true);
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🏢 Business Management</h1>
          <p style={styles.subtitle}>Bütün business-ləri idarə edin</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.btnExport}>
            <FaFileExcel /> Export Excel
          </button>
          <button style={styles.btnAdd}>
            <FaPlus /> Yeni Business
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.searchBox}>
          <FaSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Ad, email və ya şəhər ilə axtarış..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">Hamısı</option>
          <option value="active">Aktiv</option>
          <option value="inactive">Deaktiv</option>
        </select>

        <div style={styles.dateRangeWrap}>
          <span style={styles.dateRangeLabel}>📅 Qeydiyyat tarixi:</span>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={styles.dateInput}
            title="Başlanğıc tarix"
          />
          <span style={{ color: '#999', fontSize: 13 }}>—</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={styles.dateInput}
            title="Son tarix"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              style={styles.dateClearBtn}
              title="Tarixi sıfırla"
            >✕</button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Ümumi:</span>
          <span style={styles.statValue}>{businesses.length}</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Aktiv:</span>
          <span style={{ ...styles.statValue, color: '#4CAF50' }}>
            {businesses.filter((b) => b.is_active).length}
          </span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Deaktiv:</span>
          <span style={{ ...styles.statValue, color: '#F44336' }}>
            {businesses.filter((b) => !b.is_active).length}
          </span>
        </div>
      </div>

      {/* Table */}
      <BusinessTable
        businesses={filteredBusinesses}
        onEdit={handleEdit}
        onKillSwitch={handleKillSwitch}
        onActivate={handleActivate}
        onViewStats={handleViewStats}
        onDelete={handleDelete}
      />

      {/* Audit Modal */}
      {showAuditModal && selectedBusiness && (
        <BusinessAuditModal
          business={selectedBusiness}
          onClose={() => {
            setShowAuditModal(false);
            setSelectedBusiness(null);
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editingBusiness && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBoxWide}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>✏️ {editingBusiness.name}</h2>
              <button onClick={() => { setShowEditModal(false); setEditingBusiness(null); }} style={styles.modalCloseBtn}>✕</button>
            </div>

            <div style={styles.modalScroll}>

              {/* Əsas məlumatlar */}
              <div style={styles.modalSection}>
                <div style={styles.modalSectionTitle}>📋 Əsas Məlumatlar</div>
                <div style={styles.modalGrid2}>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Ad</label>
                    <input style={styles.modalInput} value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Şəhər</label>
                    <input style={styles.modalInput} value={editForm.city} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} />
                  </div>
                  <div style={{ ...styles.modalField, gridColumn: 'span 2' }}>
                    <label style={styles.modalLabel}>Ünvan</label>
                    <input style={styles.modalInput} value={editForm.address} onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Telefon</label>
                    <input style={styles.modalInput} value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>WhatsApp (bildiriş)</label>
                    <input style={styles.modalInput} value={editForm.notification_whatsapp} placeholder="+994501234567" onChange={e => setEditForm(p => ({ ...p, notification_whatsapp: e.target.value }))} />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Biznes növü</label>
                    <select style={styles.modalSelect} value={editForm.business_type} onChange={e => setEditForm(p => ({ ...p, business_type: e.target.value }))}>
                      <option value="cafe">☕ Kafe</option>
                      <option value="restaurant">🍽️ Restoran</option>
                      <option value="bar">🍺 Bar</option>
                      <option value="fast_food">🍔 Fast Food</option>
                      <option value="club">🎵 Klub</option>
                      <option value="hookah">💨 Qəlyan</option>
                      <option value="teahouse">🍵 Çayxana</option>
                      <option value="hotel">🏨 Otel</option>
                      <option value="bakery">🥐 Çörəkxana / Patisserie</option>
                    </select>
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Mətbəx növü</label>
                    <input style={styles.modalInput} value={editForm.cuisine_type} onChange={e => setEditForm(p => ({ ...p, cuisine_type: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* Premium & Status */}
              <div style={styles.modalSection}>
                <div style={styles.modalSectionTitle}>⭐ Premium & Status</div>
                <div style={styles.modalGrid2}>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Premium Tier (Paket)</label>
                    <select style={styles.modalSelect} value={editForm.premium_tier} onChange={e => setEditForm(p => ({ ...p, premium_tier: e.target.value }))}>
                      <option value={0}>0 — Pulsuz</option>
                      <option value={1}>1 — Starter</option>
                      <option value={2}>2 — Growth</option>
                      <option value={3}>3 — Premium</option>
                    </select>
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Reytinq (0–5)</label>
                    <input style={styles.modalInput} type="number" step="0.1" min="0" max="5" value={editForm.rating} onChange={e => setEditForm(p => ({ ...p, rating: e.target.value }))} />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Ümumi tutum</label>
                    <input style={styles.modalInput} type="number" value={editForm.total_capacity} onChange={e => setEditForm(p => ({ ...p, total_capacity: e.target.value }))} />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Servis haqqı</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input style={{ ...styles.modalInput, flex: 1 }} type="number" value={editForm.service_fee} placeholder="Məs: 10" onChange={e => setEditForm(p => ({ ...p, service_fee: e.target.value }))} />
                      <select style={{ ...styles.modalSelect, width: 110 }} value={editForm.service_fee_type} onChange={e => setEditForm(p => ({ ...p, service_fee_type: e.target.value }))}>
                        <option value="percent">% faiz</option>
                        <option value="fixed">₼ sabit</option>
                      </select>
                    </div>
                  </div>
                  <div style={styles.modalCheckRow}>
                    {[
                      { key: 'is_active',          label: '✅ Aktiv' },
                      { key: 'is_chain',            label: '🏪 Şəbəkə (chain)' },
                      { key: 'is_tourist_friendly', label: '✈️ Turistə uyğun' },
                      { key: 'is_pet_friendly',     label: '🐾 Heyvansevər' },
                    ].map(({ key, label }) => (
                      <label key={key} style={styles.checkLabel}>
                        <input type="checkbox" checked={!!editForm[key]} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.checked }))} style={{ marginRight: 6 }} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Admin Badge-ləri */}
                <div style={{ marginTop: 16 }}>
                  <label style={{ ...styles.modalLabel, display: 'block', marginBottom: 10 }}>
                    🏅 Admin Badge-ləri <span style={{ fontWeight: 400, color: '#888', fontSize: 12 }}>(çoxlu seçim olunur)</span>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {[
                      { value: 'featured',   label: '🌟 Seçilmiş' },
                      { value: 'premium',    label: '👑 Premium' },
                      { value: 'trending',   label: '🔥 Trend' },
                      { value: 'top_rated',  label: '🏆 Ən Yaxşı' },
                      { value: 'new',        label: '🆕 Yeni' },
                      { value: 'vip',        label: '💎 VIP' },
                      { value: 'popular',    label: '⚡ Populyar' },
                      { value: 'family',     label: '👨‍👩‍👧 Ailə' },
                      { value: 'romantic',   label: '🌹 Romantik' },
                      { value: 'halal',      label: '✅ Halal' },
                      { value: 'eco',        label: '🌿 Eko' },
                      { value: 'local',      label: '🏠 Yerli' },
                    ].map(b => {
                      const active = (editForm.admin_badges || []).includes(b.value);
                      return (
                        <button key={b.value} type="button"
                          onClick={() => setEditForm(p => ({
                            ...p,
                            admin_badges: active
                              ? (p.admin_badges || []).filter(x => x !== b.value)
                              : [...(p.admin_badges || []), b.value],
                          }))}
                          style={{
                            padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                            fontSize: 13, fontWeight: active ? 700 : 500,
                            background: active ? '#1E293B' : '#F1F5F9',
                            color: active ? '#fff' : '#475569',
                            transition: 'all 0.15s',
                          }}
                        >{b.label}</button>
                      );
                    })}
                  </div>
                  {(editForm.admin_badges || []).length > 0 && (
                    <p style={{ fontSize: 11, color: '#6366F1', marginTop: 8 }}>
                      Seçilmiş: {(editForm.admin_badges || []).join(', ')}
                    </p>
                  )}
                </div>
              </div>

              {/* Sosial & Konum */}
              <div style={styles.modalSection}>
                <div style={styles.modalSectionTitle}>📍 Sosial & Konum</div>
                <div style={styles.modalGrid2}>
                  <div style={{ ...styles.modalField, gridColumn: 'span 2' }}>
                    <label style={styles.modalLabel}>Instagram URL</label>
                    <input style={styles.modalInput} value={editForm.instagram_url} placeholder="https://instagram.com/..." onChange={e => setEditForm(p => ({ ...p, instagram_url: e.target.value }))} />
                  </div>
                  <div style={{ ...styles.modalField, gridColumn: 'span 2' }}>
                    <label style={styles.modalLabel}>Google Maps URL</label>
                    <input style={styles.modalInput} value={editForm.maps_url} placeholder="https://maps.app.goo.gl/..." onChange={e => setEditForm(p => ({ ...p, maps_url: e.target.value }))} />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Enlik (Latitude)</label>
                    <input style={styles.modalInput} type="number" step="any" value={editForm.latitude} placeholder="40.4093" onChange={e => setEditForm(p => ({ ...p, latitude: e.target.value }))} />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Uzunluq (Longitude)</label>
                    <input style={styles.modalInput} type="number" step="any" value={editForm.longitude} placeholder="49.8671" onChange={e => setEditForm(p => ({ ...p, longitude: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* Filiallar */}
              {editingBusiness.branches && editingBusiness.branches.length > 0 && (
                <div style={styles.modalSection}>
                  <div style={styles.modalSectionTitle}>🏪 Filiallar — Premium Tier</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {editingBusiness.branches.map(br => (
                      <div key={br.id} style={styles.branchRow}>
                        <span style={styles.branchName}>{br.name || 'Filial'}</span>
                        <select
                          style={styles.branchSelect}
                          value={editForm.branches_tiers?.[br.id] ?? 0}
                          onChange={e => setEditForm(p => ({
                            ...p,
                            branches_tiers: { ...p.branches_tiers, [br.id]: e.target.value }
                          }))}
                        >
                          <option value={0}>0 — Pulsuz</option>
                          <option value={1}>1 — Starter</option>
                          <option value={2}>2 — Growth</option>
                          <option value={3}>3 — Premium</option>
                        </select>
                      </div>
                    ))}
                    <button
                      style={styles.applyAllBtn}
                      onClick={() => {
                        const tier = editForm.premium_tier;
                        const all = (editingBusiness.branches || []).reduce((acc, br) => {
                          acc[br.id] = tier;
                          return acc;
                        }, {});
                        setEditForm(p => ({ ...p, branches_tiers: all }));
                      }}
                    >
                      Əsas tier-i ({editForm.premium_tier}) bütün filiallara tətbiq et
                    </button>
                  </div>
                </div>
              )}

              {/* Təsvir */}
              <div style={styles.modalSection}>
                <div style={styles.modalSectionTitle}>📝 Təsvir</div>
                <div style={styles.modalGrid1}>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Təsvir (AZ)</label>
                    <textarea style={styles.modalTextarea} value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} rows={3} />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Təsvir (EN)</label>
                    <textarea style={styles.modalTextarea} value={editForm.description_en} onChange={e => setEditForm(p => ({ ...p, description_en: e.target.value }))} rows={3} />
                  </div>
                </div>
              </div>

            </div>

            <div style={styles.modalActions}>
              <button onClick={() => { setShowEditModal(false); setEditingBusiness(null); }} style={styles.btnCancel}>Ləğv Et</button>
              <button onClick={handleEditSave} style={styles.btnSave}>💾 Yadda Saxla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1600px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  headerActions: {
    display: 'flex',
    gap: '15px',
  },
  btnExport: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  btnAdd: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#FF6B35',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  filters: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  searchBox: {
    flex: 1,
    minWidth: '300px',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '12px 15px 12px 45px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
  },
  dateRangeWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    backgroundColor: '#fff',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    flexWrap: 'wrap',
  },
  dateRangeLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
    whiteSpace: 'nowrap',
  },
  dateInput: {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #E0E0E0',
    borderRadius: '6px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#F9F9F9',
    color: '#333',
  },
  dateClearBtn: {
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '700',
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  filterSelect: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  stats: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
  },
  statCard: {
    flex: 1,
    padding: '15px 20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '600',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalBoxWide: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '760px',
    maxWidth: '95vw',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 16px 60px rgba(0,0,0,0.25)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 28px 16px',
    borderBottom: '1px solid #F0F0F0',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    margin: 0,
  },
  modalCloseBtn: {
    background: '#F1F5F9',
    border: 'none',
    borderRadius: '8px',
    width: 34, height: 34,
    fontSize: '16px',
    cursor: 'pointer',
    color: '#555',
    fontWeight: '700',
  },
  modalScroll: {
    overflowY: 'auto',
    padding: '20px 28px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  modalSection: {
    background: '#F8FAFC',
    borderRadius: '12px',
    padding: '16px 18px',
    border: '1px solid #E8ECF0',
  },
  modalSectionTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '14px',
  },
  modalGrid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  modalGrid1: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '12px',
  },
  modalField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  modalLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748B',
  },
  modalInput: {
    padding: '9px 12px',
    fontSize: '14px',
    border: '1.5px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#fff',
  },
  modalSelect: {
    padding: '9px 12px',
    fontSize: '14px',
    border: '1.5px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer',
    width: '100%',
  },
  modalTextarea: {
    padding: '9px 12px',
    fontSize: '14px',
    border: '1.5px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#fff',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  modalCheckRow: {
    gridColumn: 'span 2',
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    paddingTop: '4px',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    cursor: 'pointer',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 28px',
    borderTop: '1px solid #F0F0F0',
  },
  branchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #E8ECF0',
  },
  branchName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1A1A2E',
    flex: 1,
  },
  branchSelect: {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1.5px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer',
    width: '160px',
  },
  applyAllBtn: {
    padding: '8px 14px',
    backgroundColor: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  btnCancel: {
    padding: '10px 22px',
    backgroundColor: '#9E9E9E',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnSave: {
    padding: '10px 22px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default BusinessesPage;